import React, {useEffect, useState} from "react";
import {multiFactor, TotpMultiFactorGenerator} from "firebase/auth";
import {QRCodeSVG} from "qrcode.react";
import {auth} from "../../firebaseConfig";
import {Box, Button, CircularProgress, Stack, TextField, Typography} from "@mui/material";


interface MFATOTPSetupStepProps {
    email: string;
    setStep: (value: JSX.Element | null) => void;
}

const MFATOTPSetupStep: React.FC<MFATOTPSetupStepProps> = ({email, setStep}) => {
    const [verificationCode, setVerificationCode] = useState<string>("");
    const [error, setError] = useState<string>("");
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
    const [totpSecret, setTotpSecret] = useState<any>(null);

    useEffect(() => {
        const setupTOTP = async () => {
            console.info("Starting TOTP setup...");
            let error: any = null;

            try {
                const user = auth.currentUser;
                if (!user) return;

                const session = await multiFactor(user).getSession();
                const secret = await TotpMultiFactorGenerator.generateSecret(session);
                const url = secret.generateQrCodeUrl(email, "Medendo");

                setQrCodeUrl(url);
                setTotpSecret(secret);
                console.info("TOTP QR code generated successfully.");
            } catch (err) {
                error = err
                console.error("TOTP setup error:", err);
                setError("Er is een fout opgetreden bij het instellen van TOTP. Probeer het opnieuw.");
            }

            if (error?.code === "auth/requires-recent-login") {
                console.info('CALL')
                setStep(null) // go back to the password step
            }
        };

        setupTOTP();
    }, [email]);

    const handleVerifyTOTP = async () => {
        try {
            const user = auth.currentUser;
            if (!user || !totpSecret) throw new Error("Setup not initiated.");

            const multiFactorAssertion = TotpMultiFactorGenerator.assertionForEnrollment(totpSecret, verificationCode);
            await multiFactor(user).enroll(multiFactorAssertion, "TOTP Authenticator");

            console.info("TOTP enrollment successful.");
        } catch (err: any) {
            console.error("TOTP verification error:", err);
            setError("De verificatiecode is onjuist. Probeer het opnieuw.");
        }
    };

    const handleKeyPress = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            await handleVerifyTOTP();
        }
    };

    return (
        <Box sx={{p: 3, borderRadius: 2, maxWidth: 400, textAlign: "center"}}>
            <Typography variant="h6" gutterBottom mt={2}>
                Multi-Factor Authentication Setup
            </Typography>

            {error && <Typography color="error">{error}</Typography>}

            {qrCodeUrl ? (
                <Stack alignItems="center">
                    <Typography>Scan this QR code with your authenticator app:</Typography>
                    <QRCodeSVG value={qrCodeUrl} size={200}/>
                    <Typography>Enter the verification code:</Typography>

                    <TextField
                        fullWidth
                        label="Verification Code"
                        variant="outlined"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        onKeyDown={handleKeyPress}
                    />

                    <Button variant="contained" color="primary" onClick={handleVerifyTOTP}
                            disabled={!verificationCode}>
                        Verify and Enable TOTP
                    </Button>
                </Stack>
            ) : (
                <CircularProgress sx={{mt: 2}}/>
            )}
        </Box>
    );
};

export default MFATOTPSetupStep;
